'use client';

import { createContext, type PropsWithChildren, useCallback, useMemo } from 'react';
import type { Nullable } from '@libs/core';
import { AUTH_COOKIES, COOKIES } from '@libs/core';
import type {
    AddBillingAddressRequestBody,
    AddDonationRequestBody,
    AddItemRequestBody,
    AddPaymentMethodRequestBody,
    AddPrescriptionBody,
    AddShippingAddressRequestBody,
    CartUI,
    DeliveryOptionsPost,
    GuestEmailRequestBody,
    PickupInfoRequestBody,
    PlaceOrderRequestBody,
    SetShippingMethodRequestBody,
    TreatPointRequestBody,
    UpdateItemFulfillmentMethodRequestBody,
    UpdateItemQtyRequestBody,
    UpdateItemStoreRequestBody,
    UpdateShippingAddressRequestBody,
} from '@libs/common-node';
import Cookie from 'js-cookie';
import {
    addBillingAddress,
    addShippingAddress,
    addPayment,
    addDonation,
    addGuestEmail,
    addPickupInfo,
    addDiscountCode,
    removeDonation,
    removeItem,
    removeDiscountCode,
    updateItemFulfillmentMethod,
    updateItemQty,
    updateItemStore,
    placeOrder,
    removePayment as removePaymentFromCart,
    addTreatPoints as addTreatPointsToCart,
    removeTreatPoints,
    addItem as addItemToCart,
    getShippingMethods,
    getDeliveryOptions,
    setDeliveryOptions,
    validateShippingAddresses,
    setShippingMethods,
    updateShippingAddress,
    addLegacyPrescription,
    addPrescription,
    logConsoleError,
    updateSubscriptionStatus,
} from '../utils';
import { usePageLayout } from '../hooks/usePageLayout';
import type { CartContextProps } from '../types';
import { LayoutContent } from '../components/LayoutContent';
import { applyFormatPriceFraction } from '../utils/applyFormatPriceFraction';
import useCartState from './utils/useCartState';
import useTreatsInCart from './utils/useTreatsInCart';
import { canProductProceed } from './utils/canProductProceed';
import { COMPONENTS, CART_ERROR_MESSAGES } from '../constants';
import { useCartNotifications } from '../hooks';

export const CartContext = createContext<Nullable<CartContextProps>>(undefined);

export function CartProvider({ children }: PropsWithChildren) {
    const { user } = usePageLayout();
    const cartId = Cookie.get(COOKIES.CART_ID);

    const {
        cart,
        updateCartState,
        cartShippingOptionsMessage,
        setCartShippingOptionsMessage,
        errors,
        handleCartError,
        ...restOfCartState
    } = useCartState(cartId as string);

    const { setNotifications } = useCartNotifications();

    const { estimatedTreatsPoints, getEstimatedTreatsPoints, isGetEstimatedTreatsPointsLoading } = useTreatsInCart(
        cart,
        user
    );

    /**
     * Asynchronously adds an item to the cart using provided details, subject to business rules.
     *
     * The function first checks if the item can be added and if the cart state is ready (either has a valid ID and version
     * or no cart ID is set). If conditions are met, it proceeds to add the item using `addItemToCart` and updates the cart state.
     * If a new cart is created, the cart ID is stored in cookies. It also handles cart updating state by toggling `setUpdating`.
     *
     * @param {AddItemRequestBody} body - Request body for /carts/items BFF endpoint. Please refer to AddItemRequestBody type for more details.
     * @returns {Promise<Cart | null>} A promise resolving with the updated cart object or null if unable to add.
     */
    const addItem = useCallback(
        async (body: AddItemRequestBody, newCart: Nullable<CartUI>) => {
            const cartObj = newCart || cart;
            const { setUpdating, setCart } = restOfCartState;
            setUpdating(true);

            try {
                let cartIDCookie = Cookie.get(COOKIES.CART_ID);
                if (!cartObj?.id) {
                    cartIDCookie = undefined;
                }
                const isNewCart = !cartIDCookie && !cartObj?.id;

                if (!canProductProceed(cartIDCookie, cartObj)) {
                    return null;
                }

                const ctCart = await addItemToCart({
                    cartId: cartObj?.id,
                    version: cartObj?.version,
                    body,
                });

                if (ctCart?.id) {
                    setCart(ctCart);

                    if (ctCart?.notifications) {
                        setNotifications(ctCart?.notifications ?? []);
                    }

                    if (isNewCart) {
                        Cookie.set(COOKIES.CART_ID, ctCart.id);
                    }
                } else {
                    logConsoleError(CART_ERROR_MESSAGES.ADD_ITEM_FAILED, {
                        component: COMPONENTS.CART_PROVIDER,
                        severity: 'HIGH',
                        additionalInfo: `Cart: ${JSON.stringify(ctCart)}`,
                        extra: { cart: JSON.stringify(ctCart) },
                    });
                }

                return ctCart;
            } catch (e) {
                logConsoleError(JSON.stringify(e), {
                    component: COMPONENTS.CART_PROVIDER,
                    severity: 'HIGH',
                    additionalInfo: 'Exception occurred while adding item to cart',
                });
                return null;
            } finally {
                setUpdating(false);
            }
        },
        [cart, restOfCartState]
    );

    /**
     * The Cart API currently doesn't allow adding multiple products at once.
     * To enable this, we send several requests one after another.
     *
     * This approach is temporary and will be updated once the cart supports bulk operations.
     */
    const addItems = useCallback(
        async (body: AddItemRequestBody[], newCart: Nullable<CartUI>) => {
            let latestCartObject = newCart || cart;

            if (body.length === 1) {
                return await addItem(body[0], newCart);
            }

            const { setUpdating } = restOfCartState;

            for (const addItemBody of body) {
                try {
                    latestCartObject = await addItem(addItemBody, latestCartObject);
                    setUpdating(true); // addItem will setUpdating to false once done

                    // Since addItem returns null on failure, we have to explicitly throw an error ourselves
                    if (!latestCartObject) {
                        throw new Error(`Exception occured while adding product with sku ${addItemBody.sku} to cart`);
                    }
                } catch (error) {
                    logConsoleError(JSON.stringify(error), {
                        component: COMPONENTS.CART_PROVIDER,
                        severity: 'HIGH',
                        additionalInfo: 'Exception occured while adding multiple items to cart',
                    });
                    return null;
                } finally {
                    setUpdating(false);
                }
            }

            return latestCartObject;
        },
        [cart, addItem, restOfCartState]
    );

    const removeItemFromCart = useCallback(
        async (itemId: string) => updateCartState(removeItem, null, itemId),
        [updateCartState]
    );

    const removePayment = useCallback(
        async (methodId: string) =>
            updateCartState(removePaymentFromCart, {
                methodId,
            }),
        [updateCartState]
    );

    const addTreatPoints = useCallback(
        async (treatPointsDetails: TreatPointRequestBody) =>
            updateCartState(addTreatPointsToCart, {
                ...treatPointsDetails,
                loyaltyId: user?.loyaltyid || null,
            }),
        [updateCartState, user?.loyaltyid]
    );

    const getCartShippingMethods = useCallback(async () => {
        if (!cart) {
            return null;
        }

        const response = await getShippingMethods({ cartId: cart?.id });

        if (response) {
            restOfCartState.setShippingMethodsList(response);
        }

        return response;
    }, [cart]);

    const getCartDeliveryOptions = useCallback(async () => {
        if (!cart) {
            return null;
        }
        return await getDeliveryOptions({ cartId: cart?.id });
    }, [cart]);

    const contextValue = useMemo(
        () => ({
            cart: cart ? applyFormatPriceFraction(cart) : null,

            addItem,

            addItems,

            estimatedTreatsPoints,

            updateItemQuantity: (itemId: string, quantity: number) => {
                restOfCartState.clearStockErrors();

                return updateCartState<UpdateItemQtyRequestBody>(updateItemQty, { quantity }, itemId as string);
            },

            cartShippingOptionsMessage,

            changeCartShippingOptionMessage: (message: string) => {
                setCartShippingOptionsMessage(message);
            },

            updateItemFulfillmentMethod: async (props: UpdateItemFulfillmentMethodRequestBody) => {
                const { clearStockErrors } = restOfCartState;
                clearStockErrors();

                const result = await updateCartState<UpdateItemFulfillmentMethodRequestBody>(
                    updateItemFulfillmentMethod,
                    props,
                    props.itemId
                );

                return result;
            },

            addCartBillingAddress: (address: AddBillingAddressRequestBody) =>
                updateCartState<AddBillingAddressRequestBody>(
                    addBillingAddress,

                    {
                        ...address,
                    }
                ),

            addCartShippingAddress: async (address: AddShippingAddressRequestBody) => {
                const result = await updateCartState<AddShippingAddressRequestBody>(addShippingAddress, {
                    ...address,
                });
                return result;
            },

            updateCartShippingAddress: (address: UpdateShippingAddressRequestBody) =>
                updateCartState<UpdateShippingAddressRequestBody>(updateShippingAddress, {
                    ...address,
                }),

            addPayment: (paymentDetails: AddPaymentMethodRequestBody) =>
                updateCartState<AddPaymentMethodRequestBody>(addPayment, {
                    ...paymentDetails,
                }),

            removePayment,

            removeItemFromCart,

            updateStoreOnCartItem: (itemId: string, storeId: string) =>
                updateCartState(updateItemStore, { storeId } as UpdateItemStoreRequestBody, itemId),

            addCartDonation: (amount: number, fractionDigits: number) =>
                updateCartState<AddDonationRequestBody>(addDonation, {
                    amount,
                    fractionDigits,
                }),

            setCartShippingMethods: (shippingOption: SetShippingMethodRequestBody) =>
                updateCartState<SetShippingMethodRequestBody>(setShippingMethods, shippingOption),

            setCartDeliveryOptions: (deliveryOption: DeliveryOptionsPost) =>
                updateCartState<DeliveryOptionsPost>(setDeliveryOptions, deliveryOption),

            removeCartDonation: () => updateCartState(removeDonation),

            addEmail: (emailAddress: string, emailOptIn = false) =>
                updateCartState<GuestEmailRequestBody>(addGuestEmail, {
                    emailAddress,
                    emailOptIn,
                } as unknown as GuestEmailRequestBody),

            addDiscountCode: (code: string) => updateCartState(addDiscountCode, { code }),

            removeDiscountCode: async (codeId: string) => updateCartState(removeDiscountCode, { codeId }),

            addPickupInfo: async (pickupInfo: PickupInfoRequestBody) => updateCartState(addPickupInfo, pickupInfo),

            placeOrder: async (placeOrderBody: PlaceOrderRequestBody) => {
                restOfCartState.setUpdating(true);

                if (!cart) {
                    return null;
                }

                return await placeOrder({
                    cartId: cart.id,
                    body: placeOrderBody,
                    version: cart.version,
                });
            },

            addTreatPoints,

            removeTreatPoints: () => updateCartState(removeTreatPoints),

            getEstimatedTreatsPoints,

            getCartShippingMethods,

            getCartDeliveryOptions,

            ...restOfCartState,
            validateShippingAddresses: async () => {
                const isAuthenticated = Cookie.get(AUTH_COOKIES.ACCESS_TOKEN);

                if (!isAuthenticated) {
                    return null;
                }

                return validateShippingAddresses(cart?.id as string);
            },
            addLegacyPrescription: async (itemId: string, body: AddPrescriptionBody) =>
                updateCartState(addLegacyPrescription, body, itemId),

            addPrescription: async (itemId: string, body: AddPrescriptionBody) =>
                updateCartState(addPrescription, body, itemId),

            updateSubscriptionStatus: async (cartId: string) =>
                updateSubscriptionStatus({
                    cartId,
                    version: cart?.version as number,
                }),

            errors,

            handleCartError,

            setCartShippingOptionsMessage,
            isGetEstimatedTreatsPointsLoading,
        }),
        [
            cart,
            addItem,
            addItems,
            estimatedTreatsPoints,
            removePayment,
            removeItemFromCart,
            addTreatPoints,
            getEstimatedTreatsPoints,
            getCartShippingMethods,
            getCartDeliveryOptions,
            restOfCartState,
            updateCartState,
            errors,
            handleCartError,
            cartShippingOptionsMessage,
            setCartShippingOptionsMessage,
            isGetEstimatedTreatsPointsLoading,
        ]
    );

    return (
        <LayoutContent>
            <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
        </LayoutContent>
    );
}

export { useCartState };
